import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import RelatedResources from '../../../components/feature/RelatedResources';

export default function BusinessRegistrationPage() {
  const businessStructures = [
    {
      name: 'Sole Proprietorship',
      icon: 'ri-user-line',
      pros: [
        'Easiest and least expensive to establish',
        'Complete control over business decisions',
        'Simple tax filing (Schedule C)',
        'Minimal paperwork and regulations'
      ],
      cons: [
        'Unlimited personal liability',
        'Difficult to raise capital',
        'Business ends if owner dies',
        'Harder to build business credit'
      ],
      bestFor: 'Low-risk businesses, freelancers, and solo consultants'
    },
    {
      name: 'Limited Liability Company (LLC)',
      icon: 'ri-shield-check-line',
      pros: [
        'Personal liability protection',
        'Flexible tax options (pass-through or corporate)',
        'Less paperwork than corporations',
        'Credibility with customers and vendors'
      ],
      cons: [
        'More expensive than sole proprietorship',
        'Annual franchise tax in Texas',
        'Requires operating agreement',
        'Self-employment taxes on all profits'
      ],
      bestFor: 'Most small to medium businesses seeking liability protection'
    },
    {
      name: 'Corporation (C-Corp)',
      icon: 'ri-building-line',
      pros: [
        'Strongest personal liability protection',
        'Easier to raise capital through stock',
        'Unlimited life span',
        'Tax deductions for benefits'
      ],
      cons: [
        'Double taxation (corporate and personal)',
        'Complex formation and maintenance',
        'Extensive record-keeping requirements',
        'More expensive to establish and maintain'
      ],
      bestFor: 'Businesses planning to seek investors or go public'
    },
    {
      name: 'S Corporation',
      icon: 'ri-organization-chart',
      pros: [
        'Pass-through taxation (no double tax)',
        'Personal liability protection',
        'Potential tax savings on self-employment',
        'Enhanced credibility'
      ],
      cons: [
        'Strict ownership restrictions (max 100 shareholders)',
        'Must be U.S. citizens or residents',
        'Required corporate formalities',
        'IRS scrutiny on salary vs. distributions'
      ],
      bestFor: 'Profitable businesses wanting to minimize self-employment taxes'
    }
  ];

  const registrationSteps = [
    {
      number: '01',
      title: 'Choose Your Business Structure',
      description: 'Review the options above and select the structure that best fits your business goals, liability concerns, and tax situation.',
      icon: 'ri-checkbox-circle-line'
    },
    {
      number: '02',
      title: 'Register with Texas Secretary of State',
      description: 'File your Certificate of Formation online through SOSDirect. LLCs and Corporations must register; sole proprietorships only if using a DBA (Doing Business As).',
      icon: 'ri-file-text-line',
      link: 'https://www.sos.texas.gov/corp/index.shtml'
    },
    {
      number: '03',
      title: 'Obtain Federal EIN',
      description: 'Apply for an Employer Identification Number (EIN) from the IRS. Free and instant online. Required for hiring employees and opening business bank accounts.',
      icon: 'ri-government-line',
      link: 'https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online'
    },
    {
      number: '04',
      title: 'Register for Texas State Taxes',
      description: 'Register with the Texas Comptroller for sales tax permit, franchise tax, and employer taxes if applicable.',
      icon: 'ri-money-dollar-circle-line',
      link: 'https://comptroller.texas.gov/taxes/'
    },
    {
      number: '05',
      title: 'Register with El Paso City',
      description: 'Obtain your City of El Paso Business License and any required local permits specific to your industry.',
      icon: 'ri-map-pin-line'
    }
  ];

  const requiredDocuments = [
    'Valid government-issued photo ID',
    'Social Security Number or EIN',
    'Business name and address',
    'Registered agent information (for LLCs/Corporations)',
    'Operating Agreement or Corporate Bylaws',
    'Business purpose and description',
    'Owner/member/shareholder information'
  ];

  const costsTimeline = [
    {
      item: 'Texas LLC Formation',
      cost: '$300',
      timeline: '1-3 business days'
    },
    {
      item: 'Texas Corporation Formation',
      cost: '$300',
      timeline: '1-3 business days'
    },
    {
      item: 'DBA Registration (Assumed Name)',
      cost: '$25',
      timeline: 'Immediate'
    },
    {
      item: 'Federal EIN',
      cost: 'Free',
      timeline: 'Immediate online'
    },
    {
      item: 'Texas Sales Tax Permit',
      cost: 'Free',
      timeline: '1-2 weeks'
    },
    {
      item: 'El Paso Business License',
      cost: 'Varies by business type',
      timeline: '2-4 weeks'
    }
  ];

  const relatedResources = [
    {
      title: 'Local Permits & Licenses',
      description: 'Learn about required permits and licenses for your specific business type in El Paso.',
      path: '/regulations/local-permits-licenses',
      icon: 'ri-file-shield-line'
    },
    {
      title: 'Tax Requirements',
      description: 'Understand federal, state, and local tax obligations for your new business.',
      path: '/regulations/tax-requirements',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      title: 'Business Structure Guide',
      description: 'Compare LLC, Corporation, and other business structures to make the right choice.',
      path: '/resources/business-structure-guide',
      icon: 'ri-organization-chart'
    }
  ];

  return (
    <>
      <SEOHead
        title="Business Registration Guide El Paso Texas"
        description="Complete guide to registering your business in El Paso, Texas. Choose the right business structure (LLC, Corporation, Sole Proprietorship, S-Corp) and navigate the registration process with step-by-step instructions."
        canonical="/regulations/business-registration"
        keywords="El Paso business registration, Texas LLC formation, business structure El Paso, register business Texas, El Paso business license, Texas Secretary of State"
        ogImage="https://public.readdy.ai/ai/img_res/e0b28e24fadf252e884721533b3c0ce5.jpg"
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main id="main-content" className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <Link 
                to="/regulations" 
                className="text-[#D2691E] hover:underline text-sm font-medium"
                aria-label="Return to Regulations page"
              >
                ← Back to Regulations
              </Link>
            </nav>

            {/* Breadcrumb Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://elpasotexasbusiness.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Regulations",
                    "item": "https://elpasotexasbusiness.com/regulations"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Business Registration",
                    "item": "https://elpasotexasbusiness.com/regulations/business-registration"
                  }
                ]
              })}
            </script>

            {/* Header */}
            <div className="text-center mb-16">
              <div className="w-20 h-20 bg-[#D2691E] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-list-3-line text-4xl text-white"></i>
              </div>
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Business Registration</h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Your complete guide to registering your business in El Paso, Texas. Choose the right structure and navigate the registration process with confidence.
              </p>
            </div>

            {/* Business Structures */}
            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Business Structure</h2>
              <p className="text-lg text-gray-600 mb-12">
                Selecting the right business structure is one of the most important decisions you'll make. It affects your taxes, liability, and how you operate.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {businessStructures.map((structure, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${structure.icon} text-2xl text-white`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{structure.name}</h3>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-green-700 mb-3 uppercase tracking-wide">Pros</h4>
                      <ul className="space-y-2">
                        {structure.pros.map((pro, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <i className="ri-add-circle-line text-green-600 text-lg flex-shrink-0 mt-0.5"></i>
                            <span className="text-sm text-gray-700">{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-red-700 mb-3 uppercase tracking-wide">Cons</h4>
                      <ul className="space-y-2">
                        {structure.cons.map((con, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <i className="ri-indeterminate-circle-line text-red-600 text-lg flex-shrink-0 mt-0.5"></i>
                            <span className="text-sm text-gray-700">{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="pt-6 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        <strong className="text-gray-900">Best for:</strong> {structure.bestFor}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Registration Steps */}
            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Step-by-Step Registration Process</h2>
              <p className="text-lg text-gray-600 mb-12">
                Follow these steps to officially register your business in El Paso and Texas.
              </p>
              
              <div className="space-y-6">
                {registrationSteps.map((step, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start space-x-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center">
                          <span className="text-2xl font-bold text-white">{step.number}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <i className={`${step.icon} text-2xl text-[#D2691E]`}></i>
                          <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed mb-4">{step.description}</p>
                        {step.link && (
                          <a 
                            href={step.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 text-[#D2691E] hover:underline font-medium text-sm"
                          >
                            <span>Visit Official Website</span>
                            <i className="ri-external-link-line"></i>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Required Documents */}
            <section className="mb-20">
              <div className="bg-[#FAFAF8] rounded-2xl p-10">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-folder-open-line text-2xl text-white"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Required Documents Checklist</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {requiredDocuments.map((doc, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-green-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Costs & Timeline */}
            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Costs & Timeline</h2>
              <p className="text-lg text-gray-600 mb-12">
                Budget and plan accordingly with these estimated costs and processing times.
              </p>
              
              <div className="bg-[#FAFAF8] rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#D2691E]">
                      <tr>
                        <th className="px-8 py-4 text-left text-white font-bold">Item</th>
                        <th className="px-8 py-4 text-left text-white font-bold">Cost</th>
                        <th className="px-8 py-4 text-left text-white font-bold">Processing Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costsTimeline.map((item, index) => (
                        <tr key={index} className="border-b border-gray-200 last:border-b-0">
                          <td className="px-8 py-5 text-gray-900 font-medium">{item.item}</td>
                          <td className="px-8 py-5 text-gray-700">{item.cost}</td>
                          <td className="px-8 py-5 text-gray-700">{item.timeline}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* El Paso Specific Considerations */}
            <section className="mb-20">
              <div className="bg-gradient-to-br from-[#D2691E] to-[#A0522D] rounded-2xl p-10 text-white">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                  <h2 className="text-3xl font-bold">El Paso-Specific Considerations</h2>
                </div>
                
                <div className="space-y-4 text-white/90 leading-relaxed">
                  <p>
                    <strong className="text-white">Border Business Opportunities:</strong> El Paso's location on the U.S.-Mexico border creates unique opportunities for international trade and cross-border commerce. Consider how this might affect your business structure and tax obligations.
                  </p>
                  <p>
                    <strong className="text-white">Bilingual Requirements:</strong> Many El Paso businesses benefit from bilingual operations. Consider this when planning your business structure and hiring needs.
                  </p>
                  <p>
                    <strong className="text-white">Local Economic Incentives:</strong> El Paso offers various economic development incentives for new businesses. Contact the El Paso Economic Development Department to explore programs that might benefit your business structure choice.
                  </p>
                  <p>
                    <strong className="text-white">Military & Government Contracts:</strong> With Fort Bliss and other government facilities, certain business structures may be more advantageous for pursuing government contracts.
                  </p>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="text-center mb-16">
              <div className="bg-[#FAFAF8] rounded-2xl p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Help with Registration?</h2>
                <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                  Our team can guide you through the business registration process and help you choose the right structure for your El Paso business.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center space-x-2 bg-[#D2691E] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#A0522D] transition-colors whitespace-nowrap"
                  aria-label="Contact us for registration assistance"
                >
                  <span>Contact Us</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </section>
          </div>

          <RelatedResources resources={relatedResources} />
        </main>

        <Footer />
      </div>
    </>
  );
}